// src/canvas/SecurityThreatsCardV2.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Stack,
  Text,
  Group,
  Badge,
  Button,
  Paper,
  Textarea,
  TextInput,
  Select,
  ActionIcon,
  Tooltip,
  Grid,
  Divider,
  MultiSelect,
  Alert,
  Progress,
} from '@mantine/core';
import {
  IconChevronRight,
  IconChevronDown,
  IconPlus,
  IconEdit,
  IconTrash,
  IconDeviceFloppy,
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  saveScenario,
  persistScenarioName,
  persistScenarioImpact,
  persistScenarioAttackSurface,
  persistScenarioAgents,
  persistScenarioAttribute,
  deleteScenario,
  fetchSecurityControls,
} from '../redux/slices/securitySlice';
import { fetchTemplate } from '../redux/actions/templateActions';
import ThreatMitigationsInlineV2 from './components/ThreatMitigationsInlineV2';
import { authFetch } from '../utils/authFetch';

/**
 * SecurityThreatsCardV2 - Editable card UI for security threat scenarios
 * Each threat gets its own expandable card matching risk card design
 */
function SecurityThreatsCardV2({ modelId, assessmentUuid, userIsRiskManager, currentUser }) {
  const dispatch = useDispatch();

  // Track which threats are expanded (by threatId)
  const [expandedThreats, setExpandedThreats] = useState(new Set());

  // Editing state - threat name
  const [editingThreatId, setEditingThreatId] = useState(null);
  const [editedThreatName, setEditedThreatName] = useState('');

  // Editing state - CIA impact
  const [editingImpactThreatId, setEditingImpactThreatId] = useState(null);
  const [editedImpact, setEditedImpact] = useState({
    key: '',
    value: '',
    rationale: '',
  });

  // Editing state - attack surface
  const [editingAttackSurfaceThreatId, setEditingAttackSurfaceThreatId] = useState(null);

  // Editing state - threat agents
  const [editingAgentsThreatId, setEditingAgentsThreatId] = useState(null);
  const [editedAgents, setEditedAgents] = useState([]);

  // Editing state - threat agent attributes
  const [editingAttributesThreatId, setEditingAttributesThreatId] = useState(null);
  const [editedAttributes, setEditedAttributes] = useState({});

  // Add new threat form
  const [showAddThreat, setShowAddThreat] = useState(false);
  const [newThreatName, setNewThreatName] = useState('');
  const [saving, setSaving] = useState(false);

  // Get scenarios from Redux
  const scenarios = useSelector((state) => state.security?.byAssessment?.[assessmentUuid]?.scenarios || {});
  const securityControls = useSelector((state) => {
    const flatControls = state.security?.byAssessment?.[assessmentUuid]?.controls || {};
    const grouped = {};

    Object.values(flatControls).forEach((control) => {
      const scenarioId = control.scenarioId;
      if (!grouped[scenarioId]) {
        grouped[scenarioId] = [];
      }
      grouped[scenarioId].push(control);
    });

    return grouped;
  });

  // Get assessment for ownership check
  const assessment = useSelector((state) => state.assessments?.byId?.[assessmentUuid]);

  // Get template data for dropdown options (CRITICAL - don't hardcode!)
  const templateData = useSelector((state) => {
    const assessment = state.assessments?.byId?.[assessmentUuid];
    console.log('[SecurityThreatsCardV2] Full assessment object:', assessment);
    console.log('[SecurityThreatsCardV2] Assessment keys:', assessment ? Object.keys(assessment) : 'no assessment');

    // Access template_id from nested info object
    const templateId = assessment?.info?.template_id;
    console.log('[SecurityThreatsCardV2] Extracted templateId:', templateId);
    console.log('[SecurityThreatsCardV2] state.templates.byId keys:', state.templates?.byId ? Object.keys(state.templates.byId) : 'no byId');

    const result = templateId ? state.templates?.byId?.[templateId] : null;
    console.log('[SecurityThreatsCardV2] Template result:', result);
    return result;
  });

  // Fetch template on mount
  useEffect(() => {
    const templateId = assessment?.info?.template_id;
    if (templateId && !templateData) {
      console.log('[SecurityThreatsCardV2] Fetching template:', templateId);
      dispatch(fetchTemplate(templateId));
    }
  }, [dispatch, assessment, templateData]);

  // Fetch controls on mount for all scenarios
  useEffect(() => {
    const scenarioIds = Object.keys(scenarios);
    if (scenarioIds.length > 0) {
      scenarioIds.forEach((scenarioId) => {
        dispatch(fetchSecurityControls({ modelId, assessmentUuid, scenarioId }));
      });
    }
  }, [dispatch, modelId, assessmentUuid, scenarios]);

  // Extract dropdown options from template taxonomy
  const dropdownOptions = useMemo(() => {
    console.log('[SecurityThreatsCardV2] Template data:', templateData);
    console.log('[SecurityThreatsCardV2] Security taxonomy:', templateData?.taxonomy?.securityTaxonomy);

    if (!templateData?.taxonomy?.securityTaxonomy?.security) {
      console.log('[SecurityThreatsCardV2] Using fallback dropdown options - no template taxonomy');
      return {
        impactLevels: ['None', 'Negligible', 'Low', 'Moderate', 'Significant', 'High', 'Critical'], // Fallback
        threatAgents: [],
        threatAgentAttributes: {},
        vulnerabilityLevels: ['Low', 'Medium', 'High'], // Fallback
        processStrengths: ['Weak', 'Fair', 'Strong'], // Fallback
      };
    }

    const securityTaxonomy = templateData.taxonomy.securityTaxonomy.security;

    // Impact levels (for CIA triad)
    const impactConfig = securityTaxonomy.find((cat) => cat.name === 'Impact Levels');
    const impactLevels =
      impactConfig?.schema?.options || ['None', 'Negligible', 'Low', 'Moderate', 'Significant', 'High', 'Critical'];

    // Threat agents
    const agentsConfig = securityTaxonomy.find((cat) => cat.name === 'Threat Agents');
    console.log('[SecurityThreatsCardV2] Agents config:', agentsConfig);
    const threatAgents = agentsConfig?.schema?.options || [];
    const threatAgentAttributes = agentsConfig?.schema?.attributes || {};

    console.log('[SecurityThreatsCardV2] Extracted threat agents:', threatAgents);
    console.log('[SecurityThreatsCardV2] Extracted threat agent attributes:', threatAgentAttributes);

    // Extract options for each threat agent attribute
    const agentAttributeOptions = {};
    Object.entries(threatAgentAttributes).forEach(([key, value]) => {
      agentAttributeOptions[key] = value.options || [];
    });

    // Attack surface - subcategories
    const attackSurfaceConfig = securityTaxonomy.find(
      (cat) => cat.name === 'Attack Surface - Generative AI' || cat.name === 'Attack Surface - Predictive AI'
    );
    console.log('[SecurityThreatsCardV2] Attack surface config:', attackSurfaceConfig);
    console.log('[SecurityThreatsCardV2] Attack surface schema:', attackSurfaceConfig?.schema);

    // Extract subcategories (each subcategory is an attack type with multiselect options)
    const attackSurfaceSubcategories = attackSurfaceConfig?.schema?.subcategories || [];
    console.log('[SecurityThreatsCardV2] Attack surface subcategories:', attackSurfaceSubcategories);

    return {
      impactLevels,
      threatAgents,
      threatAgentAttributes: agentAttributeOptions,
      attackSurfaceSubcategories,
    };
  }, [templateData]);

  // Transform to array
  const threatList = useMemo(() => {
    return Object.values(scenarios)
      .map((scenario) => ({
        ...scenario,
        threatId: scenario.scenarioId,
        threatName: scenario.name,
        controls: securityControls[scenario.scenarioId] || [],
      }))
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }, [scenarios, securityControls]);

  // Calculate stats for header
  const threatStats = useMemo(() => {
    const totalThreats = threatList.length;
    const totalActions = threatList.reduce((sum, t) => sum + t.controls.length, 0);

    // Count threats by impact level (derived from CIA triad)
    const impactCounts = {};
    threatList.forEach((threat) => {
      const maxImpact = Math.max(
        getImpactScore(threat.impact?.confidentiality),
        getImpactScore(threat.impact?.integrity),
        getImpactScore(threat.impact?.availability)
      );
      const level = scoreToLevel(maxImpact);
      impactCounts[level] = (impactCounts[level] || 0) + 1;
    });

    // Top 3 impact levels
    const topImpacts = Object.entries(impactCounts)
      .sort((a, b) => getImpactScore(b[0]) - getImpactScore(a[0]))
      .slice(0, 3);

    return {
      totalThreats,
      totalActions,
      topImpacts,
    };
  }, [threatList]);

  // Permission check
  const canEdit = useMemo(() => {
    const isOwner = currentUser?.email === assessment?.owner_email;
    const isRM = userIsRiskManager;
    return isOwner || isRM;
  }, [currentUser, assessment, userIsRiskManager]);

  // Toggle threat expansion
  const toggleThreatExpansion = useCallback((threatId) => {
    setExpandedThreats((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(threatId)) {
        newSet.delete(threatId);
      } else {
        newSet.add(threatId);
      }
      return newSet;
    });
  }, []);

  // Add threat handler
  const handleAddThreat = useCallback(async () => {
    if (!newThreatName.trim()) {
      notifications.show({ title: 'Error', message: 'Threat name required', color: 'red' });
      return;
    }

    setSaving(true);
    try {
      // Step 1: Save to database to get scenarioId
      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          name: newThreatName.trim(),
          mappedAgents: [],
          attributes: {},
          status: 'active',
        }),
      });

      if (!response.ok) throw new Error('Failed to create threat');

      const { scenarioId } = await response.json();

      // Step 2: Update Redux
      await dispatch(
        saveScenario({
          modelId,
          scenarioId,
          name: newThreatName.trim(),
          assessmentUuid,
          status: 'active',
        })
      );

      notifications.show({ title: 'Success', message: 'Threat added', color: 'green' });
      setNewThreatName('');
      setShowAddThreat(false);
    } catch (error) {
      console.error('[SecurityThreatsCardV2] Failed to add threat:', error);
      notifications.show({ title: 'Error', message: 'Failed to add threat', color: 'red' });
    } finally {
      setSaving(false);
    }
  }, [newThreatName, modelId, assessmentUuid, dispatch]);

  // Start editing threat name
  const handleStartEditThreatName = useCallback((threat) => {
    setEditingThreatId(threat.threatId);
    setEditedThreatName(threat.threatName || '');
  }, []);

  // Save threat name
  const handleSaveThreatName = useCallback(
    async (threat) => {
      if (!editedThreatName.trim()) {
        notifications.show({ title: 'Error', message: 'Threat name required', color: 'red' });
        return;
      }

      setSaving(true);
      try {
        await dispatch(
          persistScenarioName({
            modelId,
            assessmentUuid,
            scenarioId: threat.threatId,
            name: editedThreatName.trim(),
          })
        ).unwrap();

        notifications.show({ title: 'Success', message: 'Threat updated', color: 'green' });
        setEditingThreatId(null);
      } catch (error) {
        console.error('[SecurityThreatsCardV2] Failed to update threat:', error);
        notifications.show({ title: 'Error', message: 'Failed to update threat', color: 'red' });
      } finally {
        setSaving(false);
      }
    },
    [editedThreatName, modelId, assessmentUuid, dispatch]
  );

  // Cancel edit threat name
  const handleCancelEditThreatName = useCallback(() => {
    setEditingThreatId(null);
    setEditedThreatName('');
  }, []);

  // Start editing CIA impact
  const handleStartEditImpact = useCallback((threat, impactKey) => {
    setEditingImpactThreatId(threat.threatId);
    setEditedImpact({
      key: impactKey,
      value: threat.impact?.[impactKey] || '',
      rationale: threat.impact?.[`${impactKey}Rationale`] || '',
    });
  }, []);

  // Save CIA impact
  const handleSaveImpact = useCallback(
    async (threat) => {
      setSaving(true);
      try {
        const impactUpdates = {
          [editedImpact.key]: editedImpact.value,
          [`${editedImpact.key}Rationale`]: editedImpact.rationale || '',
        };

        await dispatch(
          persistScenarioImpact({
            modelId,
            assessmentUuid,
            scenarioId: threat.threatId,
            impactUpdates,
          })
        ).unwrap();

        notifications.show({ title: 'Success', message: 'Impact updated', color: 'green' });
        setEditingImpactThreatId(null);
      } catch (error) {
        console.error('[SecurityThreatsCardV2] Failed to update impact:', error);
        notifications.show({ title: 'Error', message: 'Failed to update impact', color: 'red' });
      } finally {
        setSaving(false);
      }
    },
    [editedImpact, modelId, assessmentUuid, dispatch]
  );

  // Cancel edit impact
  const handleCancelEditImpact = useCallback(() => {
    setEditingImpactThreatId(null);
    setEditedImpact({ key: '', value: '', rationale: '' });
  }, []);

  // Start editing attack surface
  const handleStartEditAttackSurface = useCallback((threat) => {
    setEditingAttackSurfaceThreatId(threat.threatId);
  }, []);

  // Save attack surface
  const handleSaveAttackSurface = useCallback(
    async (threat, attackType, selectedOptions) => {
      try {
        await dispatch(
          persistScenarioAttackSurface({
            modelId,
            assessmentUuid,
            scenarioId: threat.threatId,
            attackType,
            rowData: selectedOptions,
          })
        ).unwrap();

        notifications.show({ title: 'Success', message: 'Attack surface updated', color: 'green' });
      } catch (error) {
        console.error('[SecurityThreatsCardV2] Failed to update attack surface:', error);
        notifications.show({ title: 'Error', message: 'Failed to update', color: 'red' });
      }
    },
    [modelId, assessmentUuid, dispatch]
  );

  // Cancel edit attack surface
  const handleCancelEditAttackSurface = useCallback(() => {
    setEditingAttackSurfaceThreatId(null);
  }, []);

  // Start editing threat agents
  const handleStartEditAgents = useCallback((threat) => {
    setEditingAgentsThreatId(threat.threatId);
    setEditedAgents(threat.mappedAgents || []);
  }, []);

  // Save threat agents
  const handleSaveAgents = useCallback(
    async (threat) => {
      setSaving(true);
      try {
        await dispatch(
          persistScenarioAgents({
            modelId,
            assessmentUuid,
            scenarioId: threat.threatId,
            mappedAgents: editedAgents,
          })
        ).unwrap();

        notifications.show({ title: 'Success', message: 'Threat agents updated', color: 'green' });
        setEditingAgentsThreatId(null);
      } catch (error) {
        console.error('[SecurityThreatsCardV2] Failed to update agents:', error);
        notifications.show({ title: 'Error', message: 'Failed to update agents', color: 'red' });
      } finally {
        setSaving(false);
      }
    },
    [editedAgents, modelId, assessmentUuid, dispatch]
  );

  // Cancel edit agents
  const handleCancelEditAgents = useCallback(() => {
    setEditingAgentsThreatId(null);
    setEditedAgents([]);
  }, []);

  // Start editing threat agent attributes
  const handleStartEditAttributes = useCallback((threat) => {
    setEditingAttributesThreatId(threat.threatId);
    setEditedAttributes(threat.attributes || {});
  }, []);

  // Save threat agent attribute
  const handleSaveAttribute = useCallback(
    async (threat, attributeName, attributeValue) => {
      setSaving(true);
      try {
        await dispatch(
          persistScenarioAttribute({
            modelId,
            assessmentUuid,
            scenarioId: threat.threatId,
            attributeName,
            attributeValue,
          })
        ).unwrap();

        notifications.show({ title: 'Success', message: 'Attribute updated', color: 'green' });
      } catch (error) {
        console.error('[SecurityThreatsCardV2] Failed to update attribute:', error);
        notifications.show({ title: 'Error', message: 'Failed to update attribute', color: 'red' });
      } finally {
        setSaving(false);
      }
    },
    [modelId, assessmentUuid, dispatch]
  );

  // Cancel edit attributes
  const handleCancelEditAttributes = useCallback(() => {
    setEditingAttributesThreatId(null);
    setEditedAttributes({});
  }, []);

  // Delete threat
  const handleDeleteThreat = useCallback(
    (threat) => {
      modals.openConfirmModal({
        title: 'Delete Threat',
        children: (
          <Stack gap="sm">
            <Text size="sm">Are you sure you want to delete this threat?</Text>
            <Paper withBorder p="sm" bg="red.0">
              <Text size="sm" fw={500}>
                {threat.threatName}
              </Text>
            </Paper>
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              This will also delete:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>All mitigation actions</li>
                <li>Attack surface assessments</li>
                <li>Impact assessments</li>
              </ul>
            </Alert>
          </Stack>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          try {
            await dispatch(
              deleteScenario({
                modelId,
                assessmentUuid,
                scenarioId: threat.threatId,
                cascade: true,
              })
            ).unwrap();

            notifications.show({ title: 'Success', message: 'Threat deleted', color: 'green' });
          } catch (error) {
            console.error('[SecurityThreatsCardV2] Failed to delete threat:', error);
            notifications.show({ title: 'Error', message: 'Failed to delete threat', color: 'red' });
          }
        },
      });
    },
    [modelId, assessmentUuid, dispatch]
  );

  return (
    <Stack gap="md">
      {/* Summary Card */}
      <Card withBorder radius="md" p="md" bg="gray.0">
        <Group justify="space-between" align="center">
          <Group gap="lg">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Threats
              </Text>
              <Text size="xl" fw={700}>
                {threatStats.totalThreats}
              </Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Actions
              </Text>
              <Text size="xl" fw={700}>
                {threatStats.totalActions}
              </Text>
            </Stack>
          </Group>

          {/* Impact level badges */}
          <Group gap="xs">
            {threatStats.topImpacts.map(([level, count]) => (
              <Tooltip key={level} label={`${level}: ${count}`}>
                <Badge
                  variant="filled"
                  size="lg"
                  style={{
                    backgroundColor: getImpactLevelColor(level),
                    color: level === 'Critical' ? 'white' : '#1A1A1A',
                    fontWeight: 700,
                    minWidth: '32px',
                    padding: '8px 10px',
                  }}
                >
                  {count}
                </Badge>
              </Tooltip>
            ))}
          </Group>

          <Button leftSection={<IconPlus size={16} />} onClick={() => setShowAddThreat(true)} disabled={!canEdit}>
            Add Threat
          </Button>
        </Group>
      </Card>

      {/* Add new threat form */}
      {showAddThreat && (
        <Card withBorder radius="md" p="md" bg="blue.0">
          <Stack gap="sm">
            <Text size="sm" fw={600}>
              Add New Threat Scenario
            </Text>
            <TextInput
              placeholder="Describe the threat scenario..."
              value={newThreatName}
              onChange={(e) => setNewThreatName(e.target.value)}
              autoFocus
            />
            <Group gap="xs">
              <Button size="sm" onClick={handleAddThreat} leftSection={<IconPlus size={14} />} loading={saving}>
                Add
              </Button>
              <Button
                size="sm"
                variant="subtle"
                onClick={() => {
                  setShowAddThreat(false);
                  setNewThreatName('');
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Individual Threat Cards */}
      {threatList.length === 0 ? (
        <Card withBorder radius="md" p="xl" bg="gray.0">
          <Stack gap="sm" align="center">
            <Text size="sm" c="dimmed" fs="italic">
              No security threats identified yet.
            </Text>
            {canEdit && (
              <Button size="sm" variant="light" onClick={() => setShowAddThreat(true)}>
                Add First Threat
              </Button>
            )}
          </Stack>
        </Card>
      ) : (
        threatList.map((threat) => (
          <ThreatCard
            key={threat.threatId}
            threat={threat}
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            canEdit={canEdit}
            isExpanded={expandedThreats.has(threat.threatId)}
            onToggleExpand={() => toggleThreatExpansion(threat.threatId)}
            editingThreatId={editingThreatId}
            editedThreatName={editedThreatName}
            setEditedThreatName={setEditedThreatName}
            onStartEditThreatName={handleStartEditThreatName}
            onSaveThreatName={handleSaveThreatName}
            onCancelEditThreatName={handleCancelEditThreatName}
            editingImpactThreatId={editingImpactThreatId}
            editedImpact={editedImpact}
            setEditedImpact={setEditedImpact}
            onStartEditImpact={handleStartEditImpact}
            onSaveImpact={handleSaveImpact}
            onCancelEditImpact={handleCancelEditImpact}
            editingAttackSurfaceThreatId={editingAttackSurfaceThreatId}
            onStartEditAttackSurface={handleStartEditAttackSurface}
            onSaveAttackSurface={handleSaveAttackSurface}
            onCancelEditAttackSurface={handleCancelEditAttackSurface}
            editingAgentsThreatId={editingAgentsThreatId}
            editedAgents={editedAgents}
            setEditedAgents={setEditedAgents}
            onStartEditAgents={handleStartEditAgents}
            onSaveAgents={handleSaveAgents}
            onCancelEditAgents={handleCancelEditAgents}
            editingAttributesThreatId={editingAttributesThreatId}
            editedAttributes={editedAttributes}
            setEditedAttributes={setEditedAttributes}
            onStartEditAttributes={handleStartEditAttributes}
            onSaveAttribute={handleSaveAttribute}
            onCancelEditAttributes={handleCancelEditAttributes}
            onDeleteThreat={handleDeleteThreat}
            dropdownOptions={dropdownOptions}
            saving={saving}
          />
        ))
      )}
    </Stack>
  );
}

SecurityThreatsCardV2.propTypes = {
  modelId: PropTypes.string.isRequired,
  assessmentUuid: PropTypes.string.isRequired,
  userIsRiskManager: PropTypes.bool.isRequired,
  currentUser: PropTypes.object,
};

// Helper functions for impact level colors and scoring
const getImpactLevelColor = (level) => {
  const colors = {
    Critical: '#e03131',
    High: '#f76707',
    Significant: '#fab005',
    Moderate: '#4dabf7',
    Low: '#51cf66',
    Negligible: '#adb5bd',
    None: '#adb5bd',
  };
  return colors[level] || '#adb5bd';
};

const getImpactScore = (level) => {
  const scores = {
    Critical: 7,
    High: 6,
    Significant: 5,
    Moderate: 4,
    Low: 3,
    Negligible: 2,
    None: 1,
  };
  return scores[level] || 0;
};

const scoreToLevel = (score) => {
  if (score >= 7) return 'Critical';
  if (score >= 6) return 'High';
  if (score >= 5) return 'Significant';
  if (score >= 4) return 'Moderate';
  if (score >= 3) return 'Low';
  if (score >= 2) return 'Negligible';
  return 'None';
};


// ThreatCard - Individual expandable threat card (matching risk card design)
function ThreatCard({
  threat,
  modelId,
  assessmentUuid,
  canEdit,
  isExpanded,
  onToggleExpand,
  editingThreatId,
  editedThreatName,
  setEditedThreatName,
  onStartEditThreatName,
  onSaveThreatName,
  onCancelEditThreatName,
  editingImpactThreatId,
  editedImpact,
  setEditedImpact,
  onStartEditImpact,
  onSaveImpact,
  onCancelEditImpact,
  editingAttackSurfaceThreatId,
  onStartEditAttackSurface,
  onSaveAttackSurface,
  onCancelEditAttackSurface,
  editingAgentsThreatId,
  editedAgents,
  setEditedAgents,
  onStartEditAgents,
  onSaveAgents,
  onCancelEditAgents,
  editingAttributesThreatId,
  editedAttributes,
  setEditedAttributes,
  onStartEditAttributes,
  onSaveAttribute,
  onCancelEditAttributes,
  onDeleteThreat,
  dropdownOptions,
  saving,
}) {
  // Calculate max impact for badge
  const maxImpact = Math.max(
    getImpactScore(threat.impact?.confidentiality),
    getImpactScore(threat.impact?.integrity),
    getImpactScore(threat.impact?.availability)
  );
  const impactLevel = scoreToLevel(maxImpact);

  // Mock progress - will be replaced with real data later
  const progressPercentage = 0; // TODO: Calculate based on control completion

  const caret = isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />;

  return (
    <Card
      withBorder
      radius="md"
      p={isExpanded ? 'lg' : 'md'}
      style={{
        transition: 'all 0.3s ease',
        minHeight: isExpanded ? '400px' : 'auto',
      }}
    >
      <Stack gap="md">
        {/* Header Section */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" align="center" onClick={onToggleExpand} style={{ cursor: 'pointer', flex: 1 }}>
            {caret}
            <Stack gap={2}>
              <Text fw={700} size={isExpanded ? 'lg' : 'md'}>
                {threat.threatName}
              </Text>
              <Text size="xs" c="dimmed">
                {threat.controls.length} action{threat.controls.length !== 1 ? 's' : ''} planned
                {threat.mappedAgents && threat.mappedAgents.length > 0 && ` • ${threat.mappedAgents.length} agent${threat.mappedAgents.length !== 1 ? 's' : ''}`}
              </Text>
            </Stack>
          </Group>

          {/* Impact Counter Badge */}
          <Tooltip label={`Impact: ${impactLevel}`}>
            <Badge
              variant="filled"
              size="lg"
              style={{
                backgroundColor: getImpactLevelColor(impactLevel),
                color: ['Critical', 'High'].includes(impactLevel) ? 'white' : '#1A1A1A',
                fontWeight: 700,
                minWidth: '32px',
                padding: '8px 10px',
              }}
            >
              {impactLevel}
            </Badge>
          </Tooltip>
        </Group>

        {/* Progress Section - Always Visible (like risk cards) */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Mitigation Progress
            </Text>
            <Text size="sm" c="dimmed">
              {progressPercentage}%
            </Text>
          </Group>
          <Progress
            value={progressPercentage}
            color={progressPercentage > 75 ? 'green' : progressPercentage > 50 ? 'yellow' : 'red'}
            size="sm"
            radius="md"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              0 of {threat.controls.length} actions complete
            </Text>
            <Text size="xs" c="dimmed">
              0 evaluated
            </Text>
          </Group>
        </Stack>

        {/* Quick Actions - Collapsed View */}
        {!isExpanded && (
          <>
            <Divider />
            <Group justify="center">
              <Tooltip label="Expand to view threat details">
                <Button size="xs" variant="light" leftSection={<IconEdit size={14} />} onClick={onToggleExpand}>
                  Edit Threat
                </Button>
              </Tooltip>
            </Group>
          </>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <>
            <Divider />

            {/* Delete button in expanded view */}
            <Group justify="flex-end">
              {canEdit && (
                <Tooltip label="Delete threat">
                  <ActionIcon size="sm" variant="subtle" color="red" onClick={() => onDeleteThreat(threat)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>

            {/* Threat Name Editing */}
            {editingThreatId === threat.threatId ? (
              <Stack gap="xs">
                <Text size="sm" fw={600} c="dimmed">
                  Threat Scenario
                </Text>
                <Textarea
                  value={editedThreatName}
                  onChange={(e) => setEditedThreatName(e.target.value)}
                  placeholder="Threat scenario description..."
                  minRows={3}
                  autoFocus
                />
                <Group gap="xs">
                  <Button
                    size="xs"
                    onClick={() => onSaveThreatName(threat)}
                    leftSection={<IconDeviceFloppy size={12} />}
                    loading={saving}
                  >
                    Save
                  </Button>
                  <Button size="xs" variant="subtle" onClick={onCancelEditThreatName} disabled={saving}>
                    Cancel
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs" style={{ flex: 1 }}>
                  <Text size="sm" fw={600} c="dimmed">
                    Threat Scenario
                  </Text>
                  <Text size="sm">{threat.threatName}</Text>
                  {threat.category && (
                    <Badge size="sm" variant="dot" color="violet">
                      {threat.category}
                    </Badge>
                  )}
                </Stack>
                {canEdit && (
                  <Tooltip label="Edit threat">
                    <ActionIcon size="sm" variant="subtle" onClick={() => onStartEditThreatName(threat)}>
                      <IconEdit size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )}

            <Divider />

            {/* CIA Impact Editing */}
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                CIA Triad Impact
              </Text>
              <Grid>
                <Grid.Col span={4}>
                  <CIAImpactField
                    label="Confidentiality"
                    value={threat.impact?.confidentiality}
                    rationale={threat.impact?.confidentialityRationale}
                    threatId={threat.threatId}
                    impactKey="confidentiality"
                    isEditing={editingImpactThreatId === threat.threatId && editedImpact.key === 'confidentiality'}
                    editedValue={editedImpact.value}
                    editedRationale={editedImpact.rationale}
                    onStartEdit={onStartEditImpact}
                    onSave={onSaveImpact}
                    onCancel={onCancelEditImpact}
                    onValueChange={(val) => setEditedImpact({ ...editedImpact, value: val })}
                    onRationaleChange={(val) => setEditedImpact({ ...editedImpact, rationale: val })}
                    canEdit={canEdit}
                    impactOptions={dropdownOptions.impactLevels}
                    threat={threat}
                    saving={saving}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <CIAImpactField
                    label="Integrity"
                    value={threat.impact?.integrity}
                    rationale={threat.impact?.integrityRationale}
                    threatId={threat.threatId}
                    impactKey="integrity"
                    isEditing={editingImpactThreatId === threat.threatId && editedImpact.key === 'integrity'}
                    editedValue={editedImpact.value}
                    editedRationale={editedImpact.rationale}
                    onStartEdit={onStartEditImpact}
                    onSave={onSaveImpact}
                    onCancel={onCancelEditImpact}
                    onValueChange={(val) => setEditedImpact({ ...editedImpact, value: val })}
                    onRationaleChange={(val) => setEditedImpact({ ...editedImpact, rationale: val })}
                    canEdit={canEdit}
                    impactOptions={dropdownOptions.impactLevels}
                    threat={threat}
                    saving={saving}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <CIAImpactField
                    label="Availability"
                    value={threat.impact?.availability}
                    rationale={threat.impact?.availabilityRationale}
                    threatId={threat.threatId}
                    impactKey="availability"
                    isEditing={editingImpactThreatId === threat.threatId && editedImpact.key === 'availability'}
                    editedValue={editedImpact.value}
                    editedRationale={editedImpact.rationale}
                    onStartEdit={onStartEditImpact}
                    onSave={onSaveImpact}
                    onCancel={onCancelEditImpact}
                    onValueChange={(val) => setEditedImpact({ ...editedImpact, value: val })}
                    onRationaleChange={(val) => setEditedImpact({ ...editedImpact, rationale: val })}
                    canEdit={canEdit}
                    impactOptions={dropdownOptions.impactLevels}
                    threat={threat}
                    saving={saving}
                  />
                </Grid.Col>
              </Grid>
            </Stack>

            <Divider />

            {/* Threat Agents Editing */}
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Threat Agents
                </Text>
                {canEdit && editingAgentsThreatId !== threat.threatId && (
                  <ActionIcon size="xs" variant="light" onClick={() => onStartEditAgents(threat)}>
                    <IconEdit size={12} />
                  </ActionIcon>
                )}
              </Group>
              {editingAgentsThreatId === threat.threatId ? (
                <Stack gap="xs">
                  <MultiSelect
                    data={dropdownOptions.threatAgents}
                    value={editedAgents}
                    onChange={setEditedAgents}
                    placeholder="Select threat agents..."
                    searchable
                  />
                  <Group gap="xs">
                    <Button size="xs" onClick={() => onSaveAgents(threat)} loading={saving}>
                      Save
                    </Button>
                    <Button size="xs" variant="subtle" onClick={onCancelEditAgents} disabled={saving}>
                      Cancel
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <>
                  {(threat.mappedAgents || []).length === 0 ? (
                    <Text size="xs" c="dimmed" fs="italic">
                      No threat agents mapped
                    </Text>
                  ) : (
                    <Group gap="xs">
                      {(threat.mappedAgents || []).map((agent) => (
                        <Badge key={agent} variant="dot" color="violet">
                          {agent}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  {/* Threat Agent Attributes Section */}
                  {(threat.attributes && Object.keys(threat.attributes).length > 0) || canEdit ? (
                    <Paper withBorder p="xs" bg="gray.0" mt="xs">
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="xs" fw={600} c="dimmed">
                            Agent Attributes
                          </Text>
                          {canEdit && editingAttributesThreatId !== threat.threatId && (
                            <ActionIcon size="xxs" variant="subtle" onClick={() => onStartEditAttributes(threat)}>
                              <IconEdit size={10} />
                            </ActionIcon>
                          )}
                        </Group>

                        {editingAttributesThreatId === threat.threatId ? (
                          // EDIT MODE - Show MultiSelect for each attribute
                          <Stack gap="xs">
                            {Object.entries(dropdownOptions.threatAgentAttributes || {}).map(([attrName, attrOptions]) => (
                              <Stack key={attrName} gap={4}>
                                <Text size="xs" c="dimmed">
                                  {attrName}
                                </Text>
                                <MultiSelect
                                  data={attrOptions}
                                  value={Array.isArray(editedAttributes[attrName]) ? editedAttributes[attrName] : []}
                                  onChange={(values) => {
                                    const newAttrs = { ...editedAttributes, [attrName]: values };
                                    setEditedAttributes(newAttrs);
                                  }}
                                  placeholder={`Select ${attrName.toLowerCase()}...`}
                                  size="xs"
                                  searchable
                                />
                              </Stack>
                            ))}
                            <Group gap="xs" mt="xs">
                              <Button
                                size="xs"
                                onClick={async () => {
                                  // Save all attributes at once
                                  for (const [attrName, attrValue] of Object.entries(editedAttributes)) {
                                    await onSaveAttribute(threat, attrName, attrValue);
                                  }
                                  onCancelEditAttributes();
                                }}
                                loading={saving}
                              >
                                Save
                              </Button>
                              <Button size="xs" variant="subtle" onClick={onCancelEditAttributes} disabled={saving}>
                                Cancel
                              </Button>
                            </Group>
                          </Stack>
                        ) : (
                          // VIEW MODE - Show attribute values
                          threat.attributes && Object.keys(threat.attributes).length > 0 ? (
                            Object.entries(threat.attributes).map(([key, value]) => (
                              <Group key={key} gap="xs">
                                <Text size="xs" c="dimmed">
                                  {key}:
                                </Text>
                                <Group gap={4}>
                                  {Array.isArray(value) ? (
                                    value.map((v) => (
                                      <Badge key={v} size="xs" variant="light" color="violet">
                                        {v}
                                      </Badge>
                                    ))
                                  ) : (
                                    <Text size="xs">{value}</Text>
                                  )}
                                </Group>
                              </Group>
                            ))
                          ) : (
                            <Text size="xs" c="dimmed" fs="italic">
                              No attributes set
                            </Text>
                          )
                        )}
                      </Stack>
                    </Paper>
                  ) : null}
                </>
              )}
            </Stack>

            <Divider />

            {/* Attack Surface Editing */}
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Attack Surface
                </Text>
                {canEdit && editingAttackSurfaceThreatId !== threat.threatId && (
                  <ActionIcon size="xs" variant="light" onClick={() => onStartEditAttackSurface(threat)}>
                    <IconEdit size={12} />
                  </ActionIcon>
                )}
                {editingAttackSurfaceThreatId === threat.threatId && (
                  <Button size="xs" variant="subtle" onClick={onCancelEditAttackSurface}>
                    Done
                  </Button>
                )}
              </Group>
              {dropdownOptions.attackSurfaceSubcategories.length === 0 ? (
                <Text size="xs" c="dimmed" fs="italic">
                  No attack surface categories defined in template
                </Text>
              ) : (
                dropdownOptions.attackSurfaceSubcategories.map((subcategory) => (
                  <AttackSurfaceRow
                    key={subcategory.name}
                    subcategory={subcategory}
                    rowData={threat.attackSurface?.[subcategory.name] || {}}
                    threatId={threat.threatId}
                    isEditing={editingAttackSurfaceThreatId === threat.threatId}
                    onSave={onSaveAttackSurface}
                    threat={threat}
                    vulnerabilityOptions={['Very Low', 'Low', 'Medium', 'High', 'Very High']}
                    processStrengthOptions={['Very Strong', 'Strong', 'Adequate', 'Weak', 'Very Weak']}
                  />
                ))
              )}
            </Stack>

            <Divider />

            {/* Mitigation Actions */}
            <ThreatMitigationsInlineV2
              threat={threat}
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              canEdit={canEdit}
            />
          </>
        )}
      </Stack>
    </Card>
  );
}

// CIAImpactField Sub-component
function CIAImpactField({
  label,
  value,
  rationale,
  impactKey,
  threatId,
  isEditing,
  editedValue,
  editedRationale,
  onStartEdit,
  onSave,
  onCancel,
  onValueChange,
  onRationaleChange,
  canEdit,
  impactOptions,
  threat,
  saving,
}) {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {isEditing ? (
        <Stack gap="xs">
          <Select data={impactOptions} value={editedValue} onChange={onValueChange} size="xs" />
          <Textarea
            placeholder="Rationale (optional)..."
            value={editedRationale}
            onChange={(e) => onRationaleChange(e.target.value)}
            minRows={2}
            size="xs"
          />
          <Group gap="xs">
            <Button size="xs" onClick={() => onSave(threat)} leftSection={<IconDeviceFloppy size={12} />} loading={saving}>
              Save
            </Button>
            <Button size="xs" variant="subtle" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Group gap={4}>
          <Badge
            variant="filled"
            color={getImpactLevelColor(value)}
            style={{
              cursor: canEdit ? 'pointer' : 'default',
              color: ['Critical', 'High'].includes(value) ? 'white' : '#1A1A1A',
            }}
            onClick={canEdit ? () => onStartEdit(threat, impactKey) : undefined}
          >
            {value || 'Not assessed'}
          </Badge>
          {rationale && (
            <Tooltip label={rationale} multiline w={300}>
              <IconInfoCircle size={14} color="gray" style={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
          {canEdit && !value && (
            <ActionIcon size="xs" variant="light" onClick={() => onStartEdit(threat, impactKey)}>
              <IconEdit size={12} />
            </ActionIcon>
          )}
        </Group>
      )}
    </Stack>
  );
}

// AttackSurfaceRow Sub-component
function AttackSurfaceRow({
  subcategory,
  rowData,
  threatId,
  isEditing,
  onSave,
  threat,
  vulnerabilityOptions,
  processStrengthOptions,
}) {
  // Extract data from rowData object
  const vectors = Array.isArray(rowData) ? rowData : rowData.vectors || [];
  const vulnerability = rowData.vulnerability || '';
  const process = rowData.process || '';

  const handleVectorChange = (values) => {
    const updated = { vectors: values, vulnerability, process };
    onSave(threat, subcategory.name, updated);
  };

  const handleVulnerabilityChange = (value) => {
    const updated = { vectors, vulnerability: value, process };
    onSave(threat, subcategory.name, updated);
  };

  const handleProcessChange = (value) => {
    const updated = { vectors, vulnerability, process: value };
    onSave(threat, subcategory.name, updated);
  };

  return (
    <Paper withBorder p="xs" bg="gray.0">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2} style={{ flex: 1 }}>
            <Text size="sm" fw={600}>
              {subcategory.name}
            </Text>
            {subcategory.description && (
              <Text size="xs" c="dimmed" style={{ lineHeight: 1.3 }}>
                {subcategory.description}
              </Text>
            )}
          </Stack>
        </Group>

        {/* Attack Vectors */}
        <Stack gap={4}>
          <Text size="xs" c="dimmed" fw={500}>
            Attack Vectors:
          </Text>
          {isEditing ? (
            <MultiSelect
              data={subcategory.options || []}
              value={vectors}
              onChange={handleVectorChange}
              placeholder="Select attack vectors..."
              size="xs"
              searchable
            />
          ) : (
            <Group gap={4}>
              {vectors.length > 0 ? (
                vectors.map((option) => (
                  <Badge key={option} size="xs" variant="light" color="red">
                    {option}
                  </Badge>
                ))
              ) : (
                <Text size="xs" c="dimmed" fs="italic">
                  No vectors selected
                </Text>
              )}
            </Group>
          )}
        </Stack>

        {/* Evaluation Fields */}
        <Grid>
          <Grid.Col span={6}>
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={500}>
                Vulnerability:
              </Text>
              {isEditing ? (
                <Select
                  data={vulnerabilityOptions}
                  value={vulnerability}
                  onChange={handleVulnerabilityChange}
                  placeholder="Select..."
                  size="xs"
                  clearable
                />
              ) : vulnerability ? (
                <Badge size="xs" variant="light" color="orange">
                  {vulnerability}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed" fs="italic">
                  Not assessed
                </Text>
              )}
            </Stack>
          </Grid.Col>
          <Grid.Col span={6}>
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={500}>
                Process Strength:
              </Text>
              {isEditing ? (
                <Select
                  data={processStrengthOptions}
                  value={process}
                  onChange={handleProcessChange}
                  placeholder="Select..."
                  size="xs"
                  clearable
                />
              ) : process ? (
                <Badge size="xs" variant="light" color="blue">
                  {process}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed" fs="italic">
                  Not assessed
                </Text>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Paper>
  );
}

export default SecurityThreatsCardV2;
